import React, { useState, useEffect } from 'react';
import './Login.scss';
import { Button, Form, Modal} from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import {
  accountDetailsRequestAction,
  loginActionRequest,
  verify2faRequestAction,
} from '../../store/account/userActions';
import { useDispatch } from 'react-redux';
import { 
  Eye, 
  EyeOff, 
  Mail, 
  Lock, 
  Shield, 
  LogIn, 
  UserPlus, 
  Key,
  Sparkles,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

function Login({ ...props }) {
  const { loginRequest, user, verify2faRequest } = props;
  const [state, setState] = useState({
    data: {
      username: '',
      password: '',
    },
    errors: {},
  });

  const [showModal, setShowModal] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [passwordVisibility, setPasswordVisibility] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {
    if (user?.result?.two_factor_auth) {
      setShowModal(true);
    }
  }, [user?.result?.two_factor_auth]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { data } = state;
    const errors = validate();
    
    const payload = {
      data,
      history,
    };

    if (Object.keys(errors).length === 0) {
      setIsLoading(true);
      try {
        await loginRequest(payload);
        if (user?.two_factor_auth) {
          setShowModal(true);
        }
      } finally {
        setIsLoading(false);
      }
    } else {
      setState({
        ...state,
        errors,
      });
    }
  };

  const validate = () => {
    const { data } = state;
    const errors = {};
    
    if (data?.username === '') {
      errors.username = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(data.username)) {
      errors.username = 'Please enter a valid email address';
    }
    
    if (data?.password === '') {
      errors.password = 'Password is required';
    } else if (data.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    return errors;
  };

  const handleChange = (e) => {
    setState({
      data: {
        ...state.data,
        [e.target.id]: e.target.value,
      },
      errors: {
        ...state.errors,
        [e.target.id]: '',
      },
    });
  };

  const handleVerify = async () => {
    try {
      const userId = user.id;
      const is_enabled = user.two_factor_auth;
      const totp_code = inputValue.replace(/-/g, '');
      
      const payload = {
        userId: userId,
        totp_code: totp_code,
        is_enabled: is_enabled,
        history,
        loginPayload: {
          username: state.data.username,
          password: state.data.password,
        },
      };

      await verify2faRequest(payload);
      setInputValue('');
      setShowModal(false);
      dispatch(verify2faRequestAction({ userId, totp_code, is_enabled, history }));
    } catch (error) {
      console.error('An error occurred:', error);
    }
  };

  const handleInputChange = (e) => {
    let value = e.target.value;
    value = value.replace(/-/g, '');
    value = value.replace(/[^0-9]/g, '').slice(0, 6);
    if (value.length > 3) {
      value = value.slice(0, 3) + '-' + value.slice(3);
    }
    setInputValue(value);
  };

  const { data, errors } = state;

  const handleModalEnterKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleVerify();
    }
  };

  return (
    <div className="modern-login-container">
      <div className="login-wrapper">
        {/* Left Side - Welcome Section */}
        <div className="welcome-section">
          <div className="welcome-content">
            <div className="brand-section">
              <div className="brand-icon">
                <Sparkles size={40} />
              </div>
              <h1 className="brand-title">MultiSignals</h1>
            </div>
            
            <div className="welcome-text">
              <h2 className="welcome-title">
                Welcome to the exciting world of 
                <span className="highlight"> MultiSignals</span>
              </h2>
              <p className="welcome-subtitle">
                Advanced AI-powered trading strategies at your fingertips
              </p>
            </div>

            <div className="features-list">
              <div className="feature-item">
                <CheckCircle size={20} className="feature-icon" />
                <span>AI-Powered Trading Bots</span>
              </div>
              <div className="feature-item">
                <CheckCircle size={20} className="feature-icon" />
                <span>Real-time Market Analysis</span>
              </div>
              <div className="feature-item">
                <CheckCircle size={20} className="feature-icon" />
                <span>Secure & Reliable Platform</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="login-section">
          <div className="login-form-container">
            <div className="form-header">
              <h2 className="form-title">Sign In</h2>
              <p className="form-subtitle">Access your trading dashboard</p>
            </div>

            <Form className="modern-login-form" onSubmit={handleSubmit}>
              <Form.Group className="form-group">
                <Form.Label className="form-label">
                  <Mail size={16} className="label-icon" />
                  Email Address
                </Form.Label>
                <div className="input-wrapper">
                  <Form.Control
                    id="username"
                    name="username"
                    type="email"
                    placeholder="Enter your email address"
                    onChange={handleChange}
                    value={data?.username}
                    className={`modern-input ${errors?.username ? 'error' : ''}`}
                  />
                  <Mail size={18} className="input-icon" />
                </div>
                {errors?.username && (
                  <div className="error-message">
                    <AlertCircle size={14} />
                    <span>{errors.username}</span>
                  </div>
                )}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label className="form-label">
                  <Lock size={16} className="label-icon" />
                  Password
                </Form.Label>
                <div className="input-wrapper">
                  <Form.Control
                    id="password"
                    type={passwordVisibility ? 'text' : 'password'}
                    placeholder="Enter your password"
                    onChange={handleChange}
                    value={data?.password}
                    className={`modern-input ${errors?.password ? 'error' : ''}`}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setPasswordVisibility(!passwordVisibility)}
                  >
                    {passwordVisibility ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors?.password && (
                  <div className="error-message">
                    <AlertCircle size={14} />
                    <span>{errors.password}</span>
                  </div>
                )}
              </Form.Group>

              <Button
                className="modern-login-btn"
                variant="primary"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="loading-spinner"></div>
                ) : (
                  <>
                    <LogIn size={18} />
                    <span className='m-2'>Sign In</span>
                  </>
                )}
              </Button>
            </Form>

            <div className="form-links">
              <NavLink className="forgot-password-link" to="/forgotpassword">
                <Key size={14} />
                Forgot Password?
              </NavLink>

              <div className="signup-section">
                <span className="signup-text">Don't have an account yet?</span>
                <NavLink className="signup-link" to="/signup">
                  <UserPlus size={16} />
                  Create Account
                </NavLink>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modern 2FA Modal */}
      <Modal 
        show={showModal} 
        onHide={() => setShowModal(false)}
        className="modern-2fa-modal"
        centered
      >
        <Modal.Header className="modal-header-modern">
          <div className="modal-title-section">
            <Shield size={24} className="modal-icon" />
            <Modal.Title className="modal-title">Two-Factor Authentication</Modal.Title>
          </div>
        </Modal.Header>
        
        <Modal.Body className="modal-body-modern">
          <div className="modal-content-wrapper">
            <p className="modal-description">
              Please enter the 6-digit code from your authenticator app
            </p>
            
            <Form.Group className="code-input-group">
              <Form.Label className="code-label">Authentication Code</Form.Label>
              <Form.Control
                type="text"
                placeholder="000-000"
                value={inputValue}
                onChange={handleInputChange}
                onKeyPress={handleModalEnterKeyPress}
                className="code-input"
                maxLength={7}
              />
            </Form.Group>
          </div>
        </Modal.Body>
        
        <Modal.Footer className="modal-footer-modern">
          <Button 
            variant="outline-secondary" 
            onClick={() => setShowModal(false)}
            className="modal-btn-secondary"
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleVerify}
            className="modal-btn-primary"
            disabled={inputValue.length < 7}
          >
            <CheckCircle size={16} />
            Verify Code
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    user: state.userReducer.user || { two_factor_auth: false },
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    loginRequest: (creds) => {
      dispatch(loginActionRequest(creds));
    },
    accountDetailsRequest: () => {
      dispatch(accountDetailsRequestAction());
    },
    verify2faRequest: (payload) => {
      dispatch(verify2faRequestAction(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Login);